import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/core/Button';
import { IconButton } from '@/shared/ui/core/IconButton';
import { Select } from '@/shared/ui/core/Select';
import { Spinner } from '@/shared/ui/core/Loading';
import { LIST_PAGE_SIZE_OPTIONS } from "@/shared/list/pagination";
import { ChevronLeft, ChevronRight } from 'lucide-react';

export type PaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** numbered | simple */
  variant?: 'numbered' | 'simple';
  /**
   * When true, disables navigation buttons and indicates page transition in progress.
   */
  loading?: boolean;
  /**
   * When set with `onPageSizeChange`, renders an in-bar page-size control.
   * Defaults to `LIST_PAGE_SIZE_OPTIONS` (10 / 20 / 50).
   */
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Minimum total items required to render pagination.
   * Default: 11 (automatically hidden when total <= 10).
   */
  minItems?: number;
};

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  className,
  variant = 'numbered',
  loading = false,
  pageSizeOptions = LIST_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  minItems = 11,
}: PaginationProps) {
  if (total < minItems) return null;

  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), pageCount);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  const canPrev = safePage > 1;
  const canNext = safePage < pageCount;
  const showPageSize = Boolean(onPageSizeChange) && pageSizeOptions.length > 0;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 text-caption text-text-secondary',
        className,
      )}
      aria-live="polite"
      aria-busy={loading ? "true" : undefined}
    >
      <div className="flex flex-wrap items-center gap-3">
        <p className="m-0">
          {from}–{to} / {total}
        </p>
        {showPageSize ? (
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap">Mỗi trang</span>
            <Select
              size="sm"
              rootClassName="w-auto"
              className="min-w-[4.5rem]"
              aria-label="Số dòng mỗi trang"
              value={String(pageSize)}
              options={pageSizeOptions.map((n) => ({
                value: String(n),
                label: String(n),
              }))}
              onValueChange={(v) => onPageSizeChange?.(Number(v))}
              disabled={loading}
            />
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        <IconButton
          label="Trang trước"
          icon={<ChevronLeft />}
          size="sm"
          disabled={!canPrev || loading}
          onClick={() => onPageChange(safePage - 1)}
        />
        {variant === 'numbered' ? (
          <div className="flex items-center gap-1 px-1">
            {Array.from({ length: pageCount }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === pageCount || Math.abs(p - safePage) <= 1)
              .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                if (idx > 0) {
                  const prev = arr[idx - 1];
                  if (typeof prev === 'number' && p - prev > 1) acc.push('ellipsis');
                }
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === 'ellipsis' ? (
                  <span key={`e-${idx}`} className="px-1">
                    …
                  </span>
                ) : (
                  <Button
                    key={item}
                    size="sm"
                    variant={item === safePage ? 'primary' : 'ghost'}
                    aria-current={item === safePage ? 'page' : undefined}
                    disabled={loading && item !== safePage}
                    onClick={() => onPageChange(item)}
                    className={cn(
                      'min-w-8 px-2 transition-all',
                      item === safePage
                        ? 'font-bold shadow-xs'
                        : 'text-text-secondary hover:text-text hover:bg-border/60',
                    )}
                  >
                    {item === safePage && loading ? (
                      <span className="inline-flex items-center gap-1">
                        <Spinner size="xs" />
                        <span>{item}</span>
                      </span>
                    ) : (
                      item
                    )}
                  </Button>
                ),
              )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 font-mono tabular-nums">
            {loading ? <Spinner size="xs" /> : null}
            <span>{safePage}/{pageCount}</span>
          </span>
        )}
        <IconButton
          label="Trang sau"
          icon={<ChevronRight />}
          size="sm"
          disabled={!canNext || loading}
          onClick={() => onPageChange(safePage + 1)}
        />
      </div>
    </div>
  );
}
